import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideEnvironmentInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication, Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { NbDatepickerModule, NbDialogModule, NbMenuModule, NbSidebarModule, NbThemeModule, NbTimepickerModule } from '@nebular/theme';
import { NgIdleModule } from '@ng-idle/core';
import { NgMapsGoogleModule } from '@ng-maps/google';
import { provideEffects } from '@ngrx/effects';
import { NavigationActionTiming, provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideScReCaptchaSettings } from '@semantic-components/re-captcha';
import * as Sentry from '@sentry/angular';
import { DeviceDetectorService } from 'ngx-device-detector';
import { provideNgxStripe } from 'ngx-stripe';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';

import { provideAppAbility } from '@app/auth/ability/ability.provider';
import { authInterceptor } from '@app/auth/interceptors/auth.interceptor';
import { FetchAuthInterceptor } from '@app/auth/interceptors/fetch-auth.interceptor';
import { scopeValueUsedInRolesInterceptor } from '@app/auth/interceptors/scope-value-used-in-roles.interceptor';
import { sloneekOriginInterceptor } from '@app/auth/interceptors/sloneek-origin.interceptor';
import { toastHandlerInterceptor } from '@app/auth/interceptors/toast-handler.interceptor';
import { userLocaleInterceptor } from '@app/auth/interceptors/user-locale.interceptor';
import { authEffects } from '@app/auth/store/auth.effects';
import { authReducers, authStoreName } from '@app/auth/store/auth.reducers';
import { provideIcons } from '@app/common-modules/common-icon/common-icon.provider';
import { environment } from '@app/environments/environment';
import { provideGtm } from '@app/services/integrations/gtm/gtm.provider';
import { provideJam } from '@app/services/integrations/jam/jam.provider';
import { provideSpecific } from '@app/services/integrations/specific/specific.provider';
import { provideZendesk } from '@app/services/integrations/zendesk/zendesk.provider';
import { appInitializerFactory } from '@app/shared/appInitializerFactory';
import { PRIMENG_DARK_THEME, SLONEEK_THEME } from '@app/shared/enums/sloneek-theme';
import { LocaleProvider } from '@app/shared/services/custom-locale-provider';
import { CustomTranslateLoader } from '@app/shared/services/custom-translate-loader.service';
import { FetchInterceptorService } from '@app/shared/services/fetch-interceptor.service';
import { provideNebularDayjsAdapter } from '@app/shared/services/nebular-dayjs-date.service';
import { appEffects } from '@app/store/app.effects';
import { appReducers } from '@app/store/app.reducers';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { primeNGThemePreset } from './app/theme.config';
import { initSentry } from './sentry';

import '@app/shared/dayjs-setup';

initSentry();

if (environment.production) {
  enableProdMode();
}

if (environment.removeAppLogs) {
  if (window) {
    window.console.log = (): void => {
      // Don't log anything.
    };
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    // Angular 21 enables CDK popover/top-layer mode by default; keep classic stacking to avoid Nebular dialog (CDK)
    // top-layer conflicts with PrimeNG overlays appended to body.
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
    importProvidersFrom(
      NgMapsGoogleModule.forRoot({
        libraries: ['places'],
        apiKey: environment.googleMapsApiKey,
      }),
      NbThemeModule.forRoot({ name: SLONEEK_THEME.LIGHT }),
      NbDatepickerModule.forRoot(),
      NbTimepickerModule.forRoot(),
      NbDialogModule.forRoot({ closeOnBackdropClick: false }),
      NbSidebarModule.forRoot(),
      NbMenuModule.forRoot(),
      NgIdleModule.forRoot(),
    ),
    provideAppInitializer(appInitializerFactory),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideNebularDayjsAdapter(),
    provideTranslateService({
      loader: provideTranslateLoader(CustomTranslateLoader),
    }),
    provideServiceWorker('ngsw-worker.js', { enabled: !window.location.hostname.includes('localhost') }),
    provideNgxStripe(),
    LocaleProvider,
    DeviceDetectorService,
    Title,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideScReCaptchaSettings({
      v3SiteKey: environment.recaptchaV3Key,
      languageCode: 'en',
    }),
    providePrimeNG({
      theme: {
        preset: primeNGThemePreset,
        options: {
          darkMode: false,
          darkModeSelector: `.${PRIMENG_DARK_THEME}`,
        },
      },
      ripple: true,
    }),
    provideHttpClient(
      withInterceptors([
        sloneekOriginInterceptor,
        authInterceptor,
        userLocaleInterceptor,
        toastHandlerInterceptor,
        scopeValueUsedInRolesInterceptor,
      ]),
      withFetch(),
    ),
    provideSpecific(environment.specificWorkspaceId, environment.specificEventName),
    provideGtm(environment.googleTagManager, environment.production),
    provideIcons({
      packs: [
        { name: 'far', packClass: 'far', iconClassPrefix: 'fa' },
        { name: 'flags', packClass: 'fi', iconClassPrefix: 'fi' },
        { name: 'fas', packClass: 'fas', iconClassPrefix: 'fa' },
        { name: 'brands', packClass: 'fab', iconClassPrefix: 'fa' },
      ],
      defaultPack: 'far',
    }),
    MessageService,
    DialogService,
    ConfirmationService,
    provideRouterStore({
      routerState: RouterState.Full,
      navigationActionTiming: NavigationActionTiming.PostActivation,
    }),
    provideStore(appReducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    provideState(authStoreName, authReducers),
    provideEffects([...appEffects, ...authEffects]),
    provideJam(),
    provideStoreDevtools({
      maxAge: 25,
      trace: true,
      logOnly: !isDevMode(),
      traceLimit: 75,
      connectInZone: true,
    }),
    provideZendesk('485cb949-4128-4a92-86df-72ce92ae3123'),
    FetchAuthInterceptor,
    provideAppAbility(),
    provideEnvironmentInitializer(() => {
      inject(FetchInterceptorService).registerInterceptor(inject(FetchAuthInterceptor));
    }),
    provideAnimations(),
  ],
})
  // eslint-disable-next-line no-console
  .catch(err => console.error(err));
